/**
 * <s-review-list> — Loads a product's published Judge.me reviews from Judge.me's live review feed
 * (the same feed Judge.me's own widget uses), so hidden or deleted reviews never show and new
 * approved reviews appear right away, without depending on the Judge.me storefront script.
 *
 * Attributes (on <s-review-list>):
 *   data-endpoint     — Judge.me review feed URL (written by Liquid).
 *   data-shop-domain  — The shop's myshopify domain.
 *   data-product-id   — Numeric product id.
 *   data-per-page     — Reviews per request (default 5).
 *   data-sort-by      — Optional Judge.me sort field (e.g. "rating").
 *   data-sort-dir     — Optional sort direction ("asc" or "desc").
 *   data-error-message — i18n text shown if the feed can't be reached.
 *
 * Required child markup:
 *   [data-review-items] — Receives the review markup.
 *   [data-review-more]  — "Show more reviews" button; revealed while more pages remain.
 *   [data-review-status] — Optional text for loading errors.
 */
class ReviewList extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector('[data-review-items]');
    this.moreButton = this.querySelector('[data-review-more]');
    this.status = this.querySelector('[data-review-status]');
    this.page = 0;
    this.perPage = Number(this.dataset.perPage) || 5;
    this.onMore = this.loadNextPage.bind(this);
    this.moreButton?.addEventListener('click', this.onMore);
    this.loadNextPage();
  }

  disconnectedCallback() {
    this.moreButton?.removeEventListener('click', this.onMore);
  }

  buildUrl(page) {
    const url = new URL(this.dataset.endpoint);
    url.searchParams.set('url', this.dataset.shopDomain);
    url.searchParams.set('shop_domain', this.dataset.shopDomain);
    url.searchParams.set('platform', 'shopify');
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('per_page', String(this.perPage));
    url.searchParams.set('page', String(page));
    if (this.dataset.sortBy) url.searchParams.set('sort_by', this.dataset.sortBy);
    if (this.dataset.sortDir) url.searchParams.set('sort_dir', this.dataset.sortDir);
    return url.toString();
  }

  async loadNextPage() {
    if (this.loading || !this.items) return;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.moreButton?.setAttribute('disabled', '');
    const nextPage = this.page + 1;
    try {
      const response = await fetch(this.buildUrl(nextPage));
      if (!response.ok) throw new Error(`Review feed failed: ${response.status}`);
      const data = await response.json();
      const template = document.createElement('template');
      template.innerHTML = data.html || '';
      const reviews = template.content.querySelectorAll('.jdgm-rev');
      reviews.forEach((review) => {
        review.querySelectorAll('img[data-src]').forEach((img) => {
          img.src = img.dataset.src;
          img.loading = 'lazy';
          img.removeAttribute('data-src');
        });
        this.items.append(review);
      });
      this.page = nextPage;
      const total = Number(data.total_count) || 0;
      const hasMore = reviews.length > 0 && this.page * this.perPage < total;
      if (this.moreButton) this.moreButton.hidden = !hasMore;
    } catch (error) {
      console.error(error);
      if (this.status && this.page === 0) this.status.textContent = this.dataset.errorMessage || '';
      if (this.moreButton) this.moreButton.hidden = true;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      this.moreButton?.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('s-review-list')) {
  customElements.define('s-review-list', ReviewList);
}
